import { FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import { SuggestionSearchBarOptionBase } from '@aibees/elephant/feature/suggestion-search-bar';
import {
  useSearchQueryFilter,
  useSearchTypeFromRoute,
} from '@aibees/elephant/hooks';
import { useLookalikeSeedsStore } from '@aibees/elephant/store/lookalike-seeds';
import { SearchType } from '@aibees/elephant/types';
import { useEffect, useMemo, useRef } from 'react';
import { useDefaultValues } from './use-default-values';

const EMPTY_SEEDS: SuggestionSearchBarOptionBase[] = [];

function sameSeeds(
  a: SuggestionSearchBarOptionBase[],
  b: SuggestionSearchBarOptionBase[]
) {
  if (a.length !== b.length) return false;
  return a.every((seed, index) => seed.value === b[index].value);
}

/**
 * Source of truth for the similarity-search seed chips.
 *
 * The URL `value` decides WHICH seeds are shown; a persisted metadata cache
 * ({@link useLookalikeSeedsStore}) supplies each seed's label/logo so chips
 * render without a browse round-trip. The backend is consulted only to resolve
 * seeds the cache is missing (cold load / shared link), and the result is merged
 * into the cache.
 *
 * Deriving the displayed chips from the URL (not from a separate "chip set"
 * store) is what makes editing reliable: removing a seed is just a URL change,
 * so a stale backend echo can never resurrect it — fixing the unreliable chip
 * "X" and the loading flash on add/remove.
 */
export function useLookalikeSeeds(): SuggestionSearchBarOptionBase[] {
  const type = useSearchTypeFromRoute() ?? SearchType.Company;
  const storeKey = String(type);
  const { params } = useSearchQueryFilter({ select: ['value'] });
  const urlValue = params.value ?? '';

  const cache = useLookalikeSeedsStore(
    (state) => state.seedsByType[storeKey] ?? EMPTY_SEEDS
  );
  const mergeSeeds = useLookalikeSeedsStore((state) => state.mergeSeeds);

  const tokens = useMemo(
    () => (urlValue ? urlValue.split(FILTER_VALUE_SEPARATOR) : []),
    [urlValue]
  );

  // Seeds the URL references but the cache can't satisfy yet. Only these need a
  // browse round-trip; during normal editing this is empty.
  const missing = useMemo(
    () => tokens.filter((token) => !cache.some((seed) => seed.value === token)),
    [tokens, cache]
  );

  const resolvedSeeds = useDefaultValues({ enabled: missing.length > 0 });

  // Cache freshly resolved metadata so subsequent renders (and a hard refresh)
  // don't need the backend again.
  useEffect(() => {
    if (!missing.length) return;
    const found = resolvedSeeds.filter((seed) => missing.includes(seed.value));
    if (found.length) mergeSeeds(storeKey, found);
  }, [missing, resolvedSeeds, mergeSeeds, storeKey]);

  // Displayed chips = URL order, metadata from cache (falling back to the
  // freshly resolved seeds while the merge above is in flight).
  const display = useMemo(
    () =>
      tokens
        .map(
          (token) =>
            cache.find((seed) => seed.value === token) ??
            resolvedSeeds.find((seed) => seed.value === token)
        )
        .filter(Boolean) as SuggestionSearchBarOptionBase[],
    [tokens, cache, resolvedSeeds]
  );

  // Keep a stable reference while the displayed values are unchanged so the
  // search bar provider's defaultValue effect doesn't wipe in-progress edits.
  const stableRef = useRef<SuggestionSearchBarOptionBase[]>(display);
  if (!sameSeeds(display, stableRef.current)) {
    stableRef.current = display;
  }

  return stableRef.current;
}
